import React, { useState, useEffect, useMemo, useContext } from 'react'

import { getLastItem } from './Select'
import { SpanPickerContext } from '../utils/initialStates'
import OperationsAutoLoader from './AutoLoader'

/**
 * This component gets all the items from an array that comes after an specif last item, in a specific intervention
 * @param allKeys {string[]} an array of keys
 * @param item {'Run (' | 'Section ('} an indication of what kind of item is being searched for
 * @param interventionId { number } - it specifies an intervention
 * @returns
 */

const getAllItemsAfterLastItem = (allKeys: string[], item: 'Run (' | 'Section (', interventionId: string) => {
    let lastItem = getLastItem(allKeys, item, interventionId)[0]
    let flag = false
    return allKeys.filter(e => {
        if (e === lastItem && e.includes('(' + interventionId + ',')) flag = true
        return flag
    })
}

/**
 * This function gets the run type of the last operation (according to start_timestamp) and returns an array of keys of
 * the runs with the same run.type and its operations
 * @param formattedTree
 * @returns string[]
 */

const getAllOperationsFromLastSectionByRunType = (formattedTree: interventionDataTypes[]): string[] => {
    let runType,
        lastSection,
        start_timestamp = 0,
        res = []

    formattedTree.forEach(intervention =>
        intervention.children.forEach(section =>
            section.children.forEach(run =>
                run.children.forEach(operation => {
                    if (operation.start_timestamp > start_timestamp) {
                        runType = run.type
                        lastSection = section
                        start_timestamp = operation.start_timestamp
                    }
                })
            )
        )
    )

    lastSection?.children
        .filter(run => runType === run.type)
        ?.forEach(run => {
            res.push(run.key)
            run.children?.forEach(operation => res.push(operation.key))
        })
    return res
}

/**
 * This component works the checking part of the tree, auto checking (if selected that config).
 * It work the state of a checker above the tree, which can check and uncheck all the tree at once.
 * @param tree {TreeComponentProps} - data related to the tree (the items on the tree, an array of keys to all the items and the default expanded nodes)
 * @param select {TreeSelectingProps} - data related to the selection of the tree (the item selected, the function to change its selection, the timespan of the selected item and the run reference of the selected item)
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

const OperationCheck = ({ tree, select }: { tree: TreeComponentProps; select: TreeSelectingProps }) => {
    const [checkedKeys, setCheckedKeys] = useState<string[]>([])
    const [mainCheckStatus, setMainCheckStatus] = useState(false)
    const [indeterminate, setIndeterminate] = useState(true)
    //TODO: remove this parameter and use only operationsPickerConfigs
    const [individualTimespans, setIndividualTimespans] = useState(true)
    const [timestampOption, setTimestampOption] = useState<OperationsPickerConfigsProps['timespanDefaultOption']>(
        'minMax'
    )
    const qtyOfCheckedOperations = useMemo((): number => checkedKeys.filter(e => e.startsWith('Operation (')).length, [
        checkedKeys
    ])
    const { operationsPickerConfigs } = useContext(SpanPickerContext)

    useEffect(() => {
        if (operationsPickerConfigs?.timespanDefaultOption)
            setTimestampOption(operationsPickerConfigs?.timespanDefaultOption)
        if (operationsPickerConfigs?.timespanDefaultOption === 'minMax') setIndividualTimespans(false)
        if (operationsPickerConfigs?.autoCheck === 'lastRun')
            setCheckedKeys(getAllItemsAfterLastItem(tree.allKeys, 'Run (', tree.formattedTree[0].id))
        if (operationsPickerConfigs?.autoCheck === 'lastSection') {
            if (operationsPickerConfigs?.autoCheckByRunType)
                setCheckedKeys(getAllOperationsFromLastSectionByRunType(tree.formattedTree))
            else setCheckedKeys(getAllItemsAfterLastItem(tree.allKeys, 'Section (', tree.formattedTree[0].id))
        }
    }, [JSON.stringify(tree.allKeys)])

    // operate between the mainCheck and the regular checkers
    const mainCheckOnChange = () => {
        if (!indeterminate) {
            mainCheckStatus ? setCheckedKeys([]) : setCheckedKeys(tree.allKeys)
            setMainCheckStatus(!mainCheckStatus)
        } else {
            setIndeterminate(false)
            setMainCheckStatus(true)
            setCheckedKeys(tree.allKeys)
        }
    }

    useEffect(() => {
        if (checkedKeys.length === tree.allKeys.length) {
            setMainCheckStatus(true)
            setIndeterminate(false)
        } else if (checkedKeys.length === 0) {
            setMainCheckStatus(false)
            setIndeterminate(false)
        } else if (checkedKeys.length > 0) {
            setIndeterminate(true)
        }
    }, [checkedKeys, JSON.stringify(tree.formattedTree)])

    const onCheck = (checkedKeysOnClick: any): void => setCheckedKeys(checkedKeysOnClick)

    return (
        <OperationsAutoLoader
            tree={tree}
            select={select}
            check={{
                mainCheck: {
                    status: mainCheckStatus,
                    setStatus: setMainCheckStatus,
                    indeterminate: indeterminate,
                    onChange: mainCheckOnChange
                },
                timestampOption: timestampOption,
                checkedKeys: checkedKeys,
                onCheck: onCheck,
                individualTimespans: individualTimespans,
                setIndividualTimespans: setIndividualTimespans,
                setTimestampOption: setTimestampOption,
                qtyOfCheckedOperations: qtyOfCheckedOperations
            }}
        />
    )
}

export default OperationCheck
